/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import './style/bluemateListCard.scss';

import classNames from 'classnames';

import React, {ReactNode} from 'react';

interface Props {
    title: ReactNode;
    /** Pale-blue pill on the right of the header band (e.g. a row count). */
    count?: ReactNode;
    /** Extra header content rendered after the count pill. */
    headerActions?: ReactNode;
    /**
     * Let the body scroll sideways when its content is wider than the card.
     * Off by default — a list that fits should never sprout a scrollbar. Turn it
     * on for table-like content whose columns cannot be squeezed further without
     * becoming unreadable (e.g. an EAN next to a full product name on a phone),
     * and give that content its own `min-width` so there is something to scroll.
     */
    scrollX?: boolean;
    className?: string;
    children: ReactNode;
}

/**
 * Bordered list/table card: white face, hairline border, rounded corners and
 * a muted header band with title + count pill. The body is unstyled — put a
 * (virtualised) row list or any table-like content in it.
 */
export default function BluemateListCard(props: Props) {
    const {title, count, headerActions, scrollX, className, children} = props;

    return (
        <div className={classNames('bluemate-list-card', {'scroll-x': scrollX}, className)}>
            <div className='list-card-head'>
                <label className='list-title'>{title}</label>
                {count != null && <span className='list-count'>{count}</span>}
                {headerActions}
            </div>
            <div className='list-card-body'>{children}</div>
        </div>
    );
}
